import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import { editPortfolioOrder, getPortfolio, removePortfolioPosition } from '../api/private';

const OrderHistory = () => {
  const [orderHistory, setOrderHistory] = useState([]);
  const [sortColumn, setSortColumn] = useState('datetime');
  const [sortOrder, setSortOrder] = useState('desc');
  const [editOrder, setEditOrder] = useState(null);
  const [updatedShares, setUpdatedShares] = useState('');
  const [updatedPrice, setUpdatedPrice] = useState('');
  const { user, isLoggedIn } = useUser();

  useEffect(() => {
    const fetchOrderHistory = async () => {
      try {
        if (isLoggedIn && user) {
          const response = await getPortfolio();
          const portfolioData = response.data.portfolio || {};
          setOrderHistory(extractOrderHistory(portfolioData));
        }
      } catch (error) {
        console.error('Failed to fetch order history', error);
      }
    };

    fetchOrderHistory();
  }, [user, isLoggedIn]);

  const formatCurrency = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return 'N/A';
    return number.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 2 });
  };

  const formatShares = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return value;
    return number.toLocaleString(undefined, { maximumFractionDigits: 4 });
  };

  const extractOrderHistory = (portfolioData) => {
    const orders = [];

    Object.keys(portfolioData).forEach((stock) => {
      portfolioData[stock]?.forEach((purchase) => {
        const shares = Number(purchase.shares);
        const purchasePrice = Number(purchase.purchasePrice);
        const tradeValue = shares * purchasePrice;

        if (shares !== 0) {
          const dateObj = new Date(purchase.datetime);
          const formattedDatetime = Number.isNaN(dateObj.getTime())
            ? purchase.datetime
            : dateObj.toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              });

          orders.push({
            ticker: stock,
            shares,
            purchasePrice,
            datetime: formattedDatetime,
            rawDatetime: purchase.datetime,
            tradeValue,
            side: shares < 0 ? 'Sell' : 'Buy',
          });
        }
      });
    });

    return orders;
  };

  const handleSort = (column) => {
    setSortOrder((prevSortOrder) => (column === sortColumn ? (prevSortOrder === 'asc' ? 'desc' : 'asc') : 'asc'));
    setSortColumn(column);
  };

  const sortedOrderHistory = [...orderHistory].sort((a, b) => {
    const aValue = a[sortColumn];
    const bValue = b[sortColumn];

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return sortOrder === 'asc'
      ? String(aValue).localeCompare(String(bValue))
      : String(bValue).localeCompare(String(aValue));
  });

  const handleEditClick = (order) => {
    setEditOrder(order);
    setUpdatedShares(Math.abs(order.shares));
    setUpdatedPrice(order.purchasePrice);
  };

  const handleSaveEdit = async () => {
    try {
      const signedShares = editOrder.side === 'Sell' ? -Math.abs(Number(updatedShares)) : Math.abs(Number(updatedShares));
      await editPortfolioOrder({
        ticker: editOrder.ticker,
        oldDatetime: editOrder.rawDatetime || editOrder.datetime,
        newDatetime: editOrder.rawDatetime || editOrder.datetime,
        shares: signedShares,
        purchasePrice: updatedPrice,
      });
      window.location.reload();
    } catch (error) {
      console.error('Failed to edit order', error);
    }
  };

  const handleRemovePosition = async (ticker, datetime) => {
    try {
      await removePortfolioPosition(ticker, datetime);
      window.location.reload();
    } catch (error) {
      console.error('Failed to remove position', error);
    }
  };

  const totalFlow = orderHistory.reduce((sum, order) => sum + Number(order.tradeValue), 0);

  const headers = [
    ['side', 'Side'],
    ['ticker', 'Ticker'],
    ['shares', 'Shares'],
    ['purchasePrice', 'Price'],
    ['tradeValue', 'Value'],
    ['datetime', 'Date'],
  ];

  return (
    <div className="orderHistoryPage" style={{ height: '100%', width: '100%' }}>
      {isLoggedIn ? (
        <div style={{ height: '100%', width: '100%' }}>
          {user && user.username ? (
            <div style={{ display: 'flex', justifyContent: 'center', height: '96%', width: '100%' }}>
              {orderHistory.length === 0 ? (
                <p style={{ fontSize: 13, color: 'rgba(0,0,0,0.55)' }}>Your order history is empty.</p>
              ) : (
                <div style={{ overflowY: 'auto', maxHeight: 500, width: '94%' }}>
                  <table style={{ width: '100%', fontSize: 12, borderCollapse: 'separate', borderSpacing: '0 6px' }}>
                    <thead style={{ position: 'sticky', top: 0, background: 'rgba(255,255,255,0.94)', backdropFilter: 'blur(10px)', zIndex: 2 }}>
                      <tr>
                        {headers.map(([key, label]) => (
                          <th
                            key={key}
                            style={{ fontWeight: 600, color: 'rgba(0,0,0,0.48)', padding: '8px 10px', textAlign: 'left', cursor: 'pointer' }}
                            onClick={() => handleSort(key)}
                          >
                            {label}{sortColumn === key ? (sortOrder === 'asc' ? ' ↑' : ' ↓') : ''}
                          </th>
                        ))}
                        <th style={{ fontWeight: 600, color: 'rgba(0,0,0,0.48)', padding: '8px 10px', textAlign: 'right' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedOrderHistory.map((order, index) => (
                        <tr key={`${order.ticker}-${order.datetime}-${index}`} style={{ background: 'rgba(250,250,255,0.86)', boxShadow: '0 1px 0 rgba(20,20,40,0.04)' }}>
                          <td style={{ padding: '9px 10px', borderRadius: '10px 0 0 10px' }}>
                            <span style={{
                              padding: '4px 8px',
                              borderRadius: 999,
                              fontSize: 11,
                              fontWeight: 700,
                              color: order.side === 'Buy' ? '#168353' : '#bf3f3f',
                              background: order.side === 'Buy' ? 'rgba(36,193,120,0.1)' : 'rgba(240,110,110,0.12)',
                            }}>
                              {order.side}
                            </span>
                          </td>
                          <td style={{ padding: '9px 10px', fontWeight: 700, border: "none" }}>{order.ticker}</td>
                          <td style={{ padding: '9px 10px', border: "none"}}>{formatShares(Math.abs(order.shares))}</td>
                          <td style={{ padding: '9px 10px', border: "none" }}>{formatCurrency(order.purchasePrice)}</td>
                          <td style={{ padding: '9px 10px', color: order.tradeValue >= 0 ? '#168353' : '#bf3f3f', border: "none" }}>{formatCurrency(Math.abs(order.tradeValue))}</td>
                          <td style={{ padding: '9px 10px', color: 'rgba(0,0,0,0.6)', border: "none" }}>{order.datetime}</td>
                          <td style={{ padding: '9px 10px', display: 'flex', justifyContent: 'space-around', border: "none", textAlign: 'right', borderRadius: '0 10px 10px 0', whiteSpace: 'nowrap' }}>
                            <button className="bin-button" onClick={() => handleEditClick(order)} title="Edit order">✎</button>
                            <button className="bin-button" onClick={() => handleRemovePosition(order.ticker, order.rawDatetime || order.datetime)} title="Remove order">×</button>
                          </td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan="4" style={{ padding: '12px 10px', color: 'rgba(0,0,0,0.45)' }}>Net recorded flow</td>
                        <td style={{ padding: '12px 10px', fontWeight: 800 }}>{formatCurrency(Math.abs(totalFlow))}</td>
                        <td colSpan="2"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
            <p>No username found. Please sign in.</p>
          )}
        </div>
      ) : (
        <div><p>User not logged in</p></div>
      )}

      {editOrder && (
        <div
          className="backgroundShade"
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(12,12,20,0.42)',
            backdropFilter: 'blur(8px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000000,
          }}
        >
          <div style={{ width: 'min(360px, 90vw)', background: 'white', padding: 22, borderRadius: 18, boxShadow: '0 24px 70px rgba(20,20,40,0.22)' }}>
            <h3 style={{ margin: '0 0 14px', fontSize: 20 }}>Edit order</h3>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 600, marginBottom: 10 }}>
              Shares
              <input type="number" value={updatedShares} onChange={(e) => setUpdatedShares(e.target.value)} style={{ height: 38, borderRadius: 10, border: '1px solid rgba(0,0,0,0.14)', padding: '0 10px' }} />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12, fontWeight: 600, marginBottom: 16 }}>
              Purchase price
              <input type="number" step="0.01" value={updatedPrice} onChange={(e) => setUpdatedPrice(e.target.value)} style={{ height: 38, borderRadius: 10, border: '1px solid rgba(0,0,0,0.14)', padding: '0 10px' }} />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setEditOrder(null)} style={{ flex: 1, height: 40, border: 'none', borderRadius: 12, background: 'rgba(0,0,0,0.07)', fontWeight: 700 }}>Cancel</button>
              <button onClick={handleSaveEdit} style={{ flex: 1, height: 40, border: 'none', borderRadius: 12, background: '#5932ea', color: 'white', fontWeight: 700 }}>Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHistory;
